import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { Fridge } from 'src/app/models/Fridge';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UserService } from '../../services/user/user.service';
import {
  InstallSection,
  InstallSectionItem,
  buildInstallSections,
  getQueryNumber,
  sortByInventoryNumber,
} from 'src/app/utils/install-label';

type StatusFilter = 'all' | 'online' | 'offline';
type BucketKey = 'online' | 'offline' | 'stopSale';

/** Fridges offline for longer than this are not listed; a search still finds them. */
const STALE_OFFLINE_MONTHS = 6;

export interface FridgeBucket {
  key: BucketKey;
  count: number;
  /** grouped by merchant group / merchant, the same as in back-office-board */
  sections: InstallSection<Fridge>[] | null;
  /** used when there are no sections (one merchant, or searching by a number) */
  items: InstallSectionItem<Fridge>[];
}

@Component({
  selector: 'app-fridge-picker',
  templateUrl: './fridge-picker.component.html',
  styleUrls: ['./fridge-picker.component.scss'],
  standalone: false,
})
export class FridgePickerComponent implements OnInit, OnChanges, OnDestroy {
  i18n = inject(TranslateService);
  private userService = inject(UserService);

  searchQuery = '';
  statusFilter: StatusFilter = 'all';
  collapsed: Record<BucketKey, boolean> = { online: false, offline: false, stopSale: false };
  buckets: FridgeBucket[] = [];
  /** fridges left out as long offline, to tell the user they exist */
  hiddenStaleCount = 0;

  @Input() fridgeList: Fridge[];
  @Output() select: EventEmitter<string> = new EventEmitter();
  @Output() open: EventEmitter<void> = new EventEmitter();
  @Output() close: EventEmitter<void> = new EventEmitter();

  @ViewChild('searchInput') searchInput: ElementRef;

  private merchantsSubscription: Subscription;

  ngOnInit() {
    // merchants and their groups load after the fridges; regroup when they arrive
    this.merchantsSubscription = this.userService.merchantsChange.subscribe(() => this.applyFilters());
    this.applyFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fridgeList) {
      this.applyFilters();
    }
  }

  ngOnDestroy() {
    this.merchantsSubscription?.unsubscribe();
  }

  async openSearch() {
    this.open.emit();
  }

  async closeSearch() {
    this.close.emit();
  }

  setStatusFilter(filter: StatusFilter) {
    this.statusFilter = filter;
    this.applyFilters();
  }

  toggleBucket(key: BucketKey) {
    this.collapsed[key] = !this.collapsed[key];
  }

  search(searchQuery: string) {
    this.searchQuery = searchQuery;
    this.applyFilters();
  }

  private applyFilters() {
    const query = (this.searchQuery || '').trim().toLowerCase().replace(/^#/, '');
    const queryNumber = getQueryNumber(query);
    let list = this.fridgeList || [];

    if (query) {
      list = list.filter((fridge) => this.matchesQuery(fridge, query));
      this.hiddenStaleCount = 0;
    } else {
      const staleBefore = new Date();
      staleBefore.setMonth(staleBefore.getMonth() - STALE_OFFLINE_MONTHS);
      const fresh = list.filter((fridge) => !this.isStaleOffline(fridge, staleBefore));
      this.hiddenStaleCount = list.length - fresh.length;
      list = fresh;
    }

    if (this.statusFilter !== 'all') {
      const connected = this.statusFilter === 'online';
      list = list.filter((fridge) => !!fridge.connected === connected);
    }

    // stop sale goes to its own list at the bottom, whatever the connection
    const isStopSale = (fridge: Fridge) => !!fridge.stopSale?.active;
    const byKey: Record<BucketKey, Fridge[]> = {
      online: list.filter((f) => !isStopSale(f) && f.connected),
      offline: list.filter((f) => !isStopSale(f) && !f.connected),
      stopSale: list.filter(isStopSale),
    };

    this.buckets = (['online', 'offline', 'stopSale'] as BucketKey[])
      .map((key) => this.buildBucket(key, byKey[key], queryNumber))
      .filter((bucket) => bucket.count > 0);
  }

  private buildBucket(key: BucketKey, fridges: Fridge[], queryNumber: string): FridgeBucket {
    // a typed number is an inventory number: a flat list with the best matches first, like in back-office-board
    const sections = queryNumber
      ? null
      : buildInstallSections(fridges, this.userService.merchantsById, this.userService.merchantGroupByMerchantId, {
          withoutGroup: this.i18n.instant('home.without_group'),
          other: this.i18n.instant('home.other_fridges'),
        });
    const items = sections
      ? []
      : sortByInventoryNumber(
          [...fridges].sort((a, b) => (a.inventoryNumber || 0) - (b.inventoryNumber || 0)),
          queryNumber,
        ).map((install) => ({ install, showMerchant: true }));

    return { key, count: fridges.length, sections, items };
  }

  private matchesQuery(fridge: Fridge, query: string): boolean {
    const merchant = this.userService.getMerchantById(fridge.merchantId as unknown as string);
    const group = this.userService.merchantGroupByMerchantId[fridge.merchantId as unknown as string];
    return [
      fridge.inventoryNumber?.toString(),
      fridge.address,
      fridge.name,
      merchant?.name,
      merchant?.siteCode,
      group?.name,
    ].some((value) => typeof value === 'string' && value.toLowerCase().includes(query));
  }

  private isStaleOffline(fridge: Fridge, staleBefore: Date): boolean {
    if (fridge.connected || !fridge.disconnectedAt) return false;
    const disconnectedAt = new Date(fridge.disconnectedAt as unknown as string);
    return !isNaN(disconnectedAt.getTime()) && disconnectedAt < staleBefore;
  }
}
